import styled, { css } from 'styled-components';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes, setFontVariant } from '@similarweb/styles';

import type { CheckboxDropdownOptionVariant } from './CheckboxDropdownOption';
import type { Placement } from './Dropdown';

const getPlacementStyles = (placement: Placement) => {
  if (placement.startsWith('on-top')) {
    const [, , side] = placement.split('-');
    return css`
      top: 0;
      ${side}: 0;
    `;
  }

  if (placement.startsWith('on-bottom')) {
    const [, , side] = placement.split('-');
    return css`
      top: -4px;
      ${side}: 0;
      transform: translateY(-100%);
    `;
  }

  return css`
    bottom: -4px;
    transform: translateY(100%);
    ${placement}: 0;
  `;
};

export const StyledDropdownContainer = styled.div<{
  placement: Placement;
  minWidth?: number | string;
  maxHeight?: number;
}>`
  position: absolute;
  z-index: 1;
  display: flex;
  flex-direction: column;
  min-width: ${({ minWidth = 246 }) => (typeof minWidth === 'string' ? minWidth : `${minWidth}px`)};
  border: 1px solid ${colorsPalettes.carbon[100]};
  background-color: ${colorsPalettes.carbon[0]};
  box-shadow: 0px 3px 5px rgba(42, 62, 82, 0.12);
  border-radius: 3px;
  overflow: hidden;

  ${({ placement }) => getPlacementStyles(placement)};
`;

export const StyledDropdownScrollContainer = styled.div<{
  maxHeight?: number;
}>`
  max-height: ${({ maxHeight = 500 }) => `${maxHeight}px`};
  overflow: auto;
  display: flex;
  flex-direction: column;
`;

export const StyledSeparator = styled.div<{ color?: string }>`
  width: 100%;
  height: 16px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    height: 1px;
    top: 50%;
    left: 10px;
    width: calc(100% - 20px);
    background-color: ${({ color = colorsPalettes.blue[400] }) => color};
  }
`;

export const StyledSearchField = styled.div`
  display: inline-flex;
  padding: 16px 16px 8px 16px;
  align-items: center;
  width: 100%;
`;

export const StyledSearchInput = styled.input`
  border: none;
  outline: none;
  ${setFontVariant('body2', colorsPalettes.carbon[400])};
  flex-grow: 1;
  margin-left: 8px;
  padding: 0;
`;

export const StyledDropdownOption = styled.button<{ isSelected?: boolean; isDisabled?: boolean }>`
  padding: 14px 16px;
  background-color: ${colorsPalettes.carbon[0]};
  ${({ isDisabled }) => setFontVariant('input2', isDisabled ? colorsPalettes.carbon[100] : colorsPalettes.carbon[500])};
  border: none;
  transition: background-color 0.2s ease-in-out;
  display: inline-flex;
  align-items: center;
  width: 100%;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};

  ${({ isSelected }) => isSelected && `background-color: ${colorsPalettes.bluegrey[100]};`}

  &:hover {
    background-color: ${({ isDisabled }) => (isDisabled ? colorsPalettes.carbon[0] : colorsPalettes.carbon[25])};
  }
`;

export const StyledDropdownButton = styled.button<{ isSelected?: boolean; isActive?: boolean }>`
  border-radius: 3px;
  background-color: ${({ isActive, isSelected }) => {
    if (isActive) {
      return colorsPalettes.bluegrey[300];
    }
    if (isSelected) {
      return colorsPalettes.carbon[50];
    }
    return colorsPalettes.carbon[25];
  }};
  padding: 8px;
  ${setFontVariant('sectionTitle', colorsPalettes.carbon[500])};
  font-weight: 500;
  line-height: 16px;
  border: 1px solid #f2f2f2;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition:
    background-color 0.2s ease-in-out,
    color 0.2s ease-in-out;
  max-height: 32px;

  ${SWReactIcons}[data-automation-icon-name='clear-circle'] {
    svg path {
      fill: ${colorsPalettes.midnight[100]};
    }
  }

  ${SWReactIcons} {
    margin-left: 8px;

    svg path {
      fill: ${colorsPalettes.carbon[500]};
      transition: fill 0.2s ease-in-out;
    }
  }

  &:hover {
    background-color: ${colorsPalettes.bluegrey[100]};
    color: ${colorsPalettes.blue[400]};
    ${SWReactIcons} svg path {
      fill: ${colorsPalettes.blue[400]};
    }
  }

  ${({ isActive }) =>
    isActive &&
    css`
      opacity: 1 !important;
      color: ${colorsPalettes.blue[400]};

      ${SWReactIcons} svg path {
        fill: ${colorsPalettes.blue[400]};
      }
    `}

  &:active {
    background-color: ${colorsPalettes.blue[200]};
  }

  &:disabled {
    cursor: not-allowed;
    background-color: ${colorsPalettes.carbon[25]};
    color: ${colorsPalettes.carbon[200]}!important;

    ${SWReactIcons} svg path {
      fill: ${colorsPalettes.carbon[200]}!important;
    }
  }
`;

export const StyledCheckboxDropdownOption = styled.button<{
  isSelected?: boolean;
  variant?: CheckboxDropdownOptionVariant;
}>`
  padding: ${({ variant }) => (variant === 'details' ? '4px 16px' : '10px 16px')};
  background-color: ${colorsPalettes.carbon[0]};
  ${setFontVariant('input2', colorsPalettes.carbon[500])};
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  display: inline-flex;
  align-items: center;
  text-align: left;
  width: 100%;

  &:hover {
    background-color: ${colorsPalettes.carbon[25]};
  }

  ${SWReactIcons} {
    flex-shrink: 0;
    margin-left: ${({ variant }) => (variant === 'details' ? 0 : 'auto')};

    svg path {
      fill: ${({ isSelected }) => (isSelected ? colorsPalettes.blue[400] : colorsPalettes.midnight[100])};
    }
  }
`;

export const StyledDropdownFooterButton = styled.button`
  width: 100%;
  padding: 10px 18px;
  border: none;
  background-color: ${colorsPalettes.blue[400]};
  ${setFontVariant('button', colorsPalettes.carbon[0])};
  text-align: right;
  font-weight: 500;
  cursor: pointer;
`;

export const StyledDropdownOptionGroup = styled.div`
  padding: 12px 16px 16px 16px;
  display: inline-flex;
  align-items: center;
  ${setFontVariant('listSectionTitle', colorsPalettes.carbon[500])};
  text-transform: uppercase;
`;

export const StyledRightSlot = styled.div`
  flex-shrink: 0;
  margin-left: auto;
`;
